/**
 * @fileoverview Countdown-to-timestamp hook.
 * @description Ticks once a second and reports the whole seconds remaining until a target
 * timestamp, recomputed from the real clock on every tick (rather than naively decrementing) so it
 * stays correct even after the tab was backgrounded and throttled.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useEffect, useState } from 'react'

/**
 * @description Computes the whole seconds remaining until targetMs, clamped to zero.
 * @param {number | null} targetMs - The target Unix-ms timestamp, or null for no active target.
 * @returns {number} Seconds remaining (0 when there's no target or it has already passed).
 */
function getRemainingSeconds(targetMs: number | null): number {
  if (!targetMs) {
    return 0
  }

  return Math.max(0, Math.ceil((targetMs - Date.now()) / 1000))
}

/**
 * @description Reports the seconds remaining until targetMs, updating once a second.
 * @param {number | null} targetMs - The target Unix-ms timestamp to count down to, or null to
 * report zero without starting a timer.
 * @returns {number} The current whole seconds remaining, clamped to zero.
 */
export function useCountdown(targetMs: number | null): number {
  const [previousTargetMs, setPreviousTargetMs] = useState(targetMs)
  const [remainingSeconds, setRemainingSeconds] = useState(() => getRemainingSeconds(targetMs))

  // Resync immediately when the target changes (e.g. a resend bumps the expiry), rather than
  // waiting up to a second for the next interval tick. Calling setState here, during render, is
  // the sanctioned way to adjust derived state in response to a changed input — see
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes.
  if (targetMs !== previousTargetMs) {
    setPreviousTargetMs(targetMs)
    setRemainingSeconds(getRemainingSeconds(targetMs))
  }

  useEffect(() => {
    if (!targetMs) {
      return
    }

    const intervalId = setInterval(() => {
      setRemainingSeconds(getRemainingSeconds(targetMs))
    }, 1000)

    return () => {
      clearInterval(intervalId)
    }
  }, [targetMs])

  return remainingSeconds
}
